import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { DataStats } from "@/components/data-stats/DataStats";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { listSchools, enqueueSchoolEnrichment, PaginatedSchoolsResponse, SchoolListItem } from "@/services/schoolsApi";
import { Button } from "@/components/ui/button";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import EnrichStaleDialog from "@/components/EnrichStaleDialog";
import { toast } from "@/hooks/use-toast";

type SortBy = "created" | "updated" | "name";

function SortHeader({
  label,
  field,
  current,
  order,
  onSort,
}: {
  label: string;
  field: SortBy;
  current: SortBy;
  order: string;
  onSort: (field: SortBy) => void;
}) {
  const active = current === field;
  return (
    <th
      className="px-3 py-2 text-left cursor-pointer select-none hover:bg-gray-100"
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        {active ? (
          order === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
        ) : (
          <ArrowUpDown className="h-3 w-3 text-muted-foreground" />
        )}
      </div>
    </th>
  );
}

export default function SchoolsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<PaginatedSchoolsResponse | null>(null);
  const [page, setPage] = useState<number>(1);
  const [size, setSize] = useState<number>(25);
  const [sortBy, setSortBy] = useState<SortBy>("created");
  const [sortOrder, setSortOrder] = useState<string>("desc");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [bulkLoading, setBulkLoading] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await listSchools(navigate, { page, size, sort_by: sortBy, sort_order: sortOrder });
        setData(res);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Erreur inconnue");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, page, size, sortBy, sortOrder]);

  const handleSort = (field: SortBy) => {
    if (sortBy === field) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder(field === "name" ? "asc" : "desc");
    }
    setPage(1);
  };

  const pageIds = useMemo(() => new Set((data?.items ?? []).map((s) => s.id)), [data]);
  const selectedCount = useMemo(
    () => Object.entries(selected).filter(([id, v]) => v && pageIds.has(Number(id))).length,
    [selected, pageIds]
  );
  const allSelectedOnPage = useMemo(
    () => (data?.items?.length ?? 0) > 0 && (data?.items ?? []).every((s) => selected[s.id]),
    [data, selected]
  );

  function toggleOne(id: number) {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  function toggleAllOnPage() {
    if (!data?.items?.length) return;
    const next = { ...selected } as Record<number, boolean>;
    const newVal = !allSelectedOnPage;
    for (const s of data.items) next[s.id] = newVal;
    setSelected(next);
  }

  const onEnqueue = async (s: SchoolListItem) => {
    try {
      const res = await enqueueSchoolEnrichment(navigate, s.id);
      toast({
        title: s.name,
        description: res.status === "already_queued" ? "Déjà en file" : "Ajouté à la file d'enrichissement",
      });
    } catch (e) {
      toast({ title: s.name, description: e instanceof Error ? e.message : "Erreur", variant: "destructive" });
    }
  };

  async function onBulkEnqueue() {
    if (!data?.items?.length) return;
    const ids = data.items.filter((s) => selected[s.id]).map((s) => s.id);
    if (!ids.length) return;
    setBulkLoading(true);
    let ok = 0;
    let already = 0;
    let failed = 0;
    for (const id of ids) {
      try {
        const res = await enqueueSchoolEnrichment(navigate, id);
        if (res.status === "already_queued") already += 1; else ok += 1;
      } catch {
        failed += 1;
      }
    }
    setBulkLoading(false);
    setSelected((prev) => {
      const copy = { ...prev };
      for (const id of ids) delete copy[id];
      return copy;
    });
    toast({
      title: "Mise en file terminée",
      description: `${ok} ajoutée(s), ${already} déjà présente(s), ${failed} échec(s).`,
      variant: failed > 0 ? "destructive" : "default",
    });
  }

  const canPrev = page > 1;
  const canNext = data ? page < data.pages : false;

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <h1 className="text-sm font-medium">Schools</h1>
          </div>
        </header>

        <div className="p-6 space-y-4">
          <DataStats entity="schools" />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-semibold">Liste des ecoles</h2>
              <EnrichStaleDialog entity="schools" label="Ecoles" />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Taille page</label>
              <select
                className="border rounded px-2 py-1 text-sm"
                value={size}
                onChange={(e) => { setSize(Number(e.target.value)); setPage(1); }}
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          </div>

          {error && <div className="text-red-600 text-sm">{error}</div>}
          {loading && <div className="text-gray-500 text-sm">Chargement...</div>}

          <div className="overflow-auto border rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left w-10">
                    <input
                      type="checkbox"
                      aria-label="Tout selectionner"
                      checked={allSelectedOnPage}
                      onChange={toggleAllOnPage}
                    />
                  </th>
                  <th className="px-3 py-2 text-left">ID</th>
                  <SortHeader label="Nom" field="name" current={sortBy} order={sortOrder} onSort={handleSort} />
                  <th className="px-3 py-2 text-left">Profil</th>
                  <SortHeader label="Cree" field="created" current={sortBy} order={sortOrder} onSort={handleSort} />
                  <SortHeader label="Modifie" field="updated" current={sortBy} order={sortOrder} onSort={handleSort} />
                  <th className="px-3 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data?.items?.map((s) => (
                  <tr key={s.id} className="border-t hover:bg-muted/50 cursor-pointer" onClick={() => navigate(`/schools/${s.id}`)}>
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={!!selected[s.id]}
                        onChange={() => toggleOne(s.id)}
                        aria-label={`Selectionner ${s.name}`}
                      />
                    </td>
                    <td className="px-3 py-2">{s.id}</td>
                    <td className="px-3 py-2 font-medium text-blue-600">{s.name}</td>
                    <td className="px-3 py-2">
                      {s.profile_url ? (
                        <a href={s.profile_url} target="_blank" rel="noreferrer" className="text-blue-600 underline" onClick={(e) => e.stopPropagation()}>
                          Voir le profil
                        </a>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2">{s.created ?? ""}</td>
                    <td className="px-3 py-2">{s.updated ?? ""}</td>
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <Button size="sm" onClick={() => onEnqueue(s)}>Enrichir</Button>
                    </td>
                  </tr>
                ))}
                {!data?.items?.length && !loading && (
                  <tr>
                    <td className="px-3 py-4 text-center text-gray-500" colSpan={7}>Aucune ecole</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {selectedCount > 0 && (
            <div className="sticky bottom-4 left-0 right-0 flex items-center justify-between gap-4 bg-white border rounded px-4 py-3 shadow">
              <div className="text-sm">
                {selectedCount} selectionnee(s) sur cette page
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => setSelected((prev) => {
                  const copy: Record<number, boolean> = { ...prev };
                  for (const id of pageIds) delete copy[id as number];
                  return copy;
                })} disabled={bulkLoading}>
                  Effacer la selection
                </Button>
                <Button onClick={onBulkEnqueue} disabled={bulkLoading}>
                  {bulkLoading ? "Envoi..." : "Pousser a l'enrichissement"}
                </Button>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Page {data?.page ?? page} / {data?.pages ?? 1} — {data?.total ?? 0} elements
            </div>
            <div className="flex gap-2">
              <Button variant="outline" disabled={!canPrev} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                Precedent
              </Button>
              <Button variant="outline" disabled={!canNext} onClick={() => setPage((p) => p + 1)}>
                Suivant
              </Button>
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
